import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import { Play, Pause, Volume2, Timer, X, Square } from "lucide-react";
import { Howl } from "howler";

const SOUND_FILES = {
  ocean: "/sounds/ocean.mp3",
  birds: "/sounds/birds.mp3",
  wind: "/sounds/wind.mp3",
  rain: "/sounds/rain.mp3",
  storm: "/sounds/storm.mp3",
  river: "/sounds/river.mp3",
  forest: "/sounds/forest.mp3",
  fire: "/sounds/fire.mp3",
  night: "/sounds/night.mp3",
};

export default function WellnessPage() {
  const { theme } = useTheme();
  const { t } = useLang();
  const isDark = theme === "dark";
  const [activeSound, setActiveSound] = useState(null);
  const [showTimer, setShowTimer] = useState(false);
  const [timerRemaining, setTimerRemaining] = useState(null);
  const soundRef = useRef(null);
  const timerRef = useRef(null);

  const SOUNDS = [
    { id: "ocean", name: t("sea"), image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&q=60" },
    { id: "birds", name: t("birds"), image: "https://images.unsplash.com/photo-1444464666168-49d633b86797?w=400&q=60" },
    { id: "wind", name: t("wind"), image: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400&q=60" },
    { id: "rain", name: t("rain"), image: "https://images.unsplash.com/photo-1515694346937-94d85e39d29c?w=400&q=60" },
    { id: "storm", name: t("storm"), image: "https://images.unsplash.com/photo-1605727216801-e27ce1d0cc28?w=400&q=60" },
    { id: "river", name: t("river"), image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&q=60" },
    { id: "forest", name: t("forest"), image: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=400&q=60" },
    { id: "fire", name: t("fireplace"), image: "https://images.unsplash.com/photo-1570993492881-25240ce854f4?w=400&q=60" },
    { id: "night", name: t("nightCalm"), image: "https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=400&q=60" },
  ];

  const textPrimary = isDark ? "text-white" : "text-slate-900";
  const textSecondary = isDark ? "text-slate-300/80" : "text-slate-600";
  const labelColor = isDark ? "text-amber-400" : "text-amber-600";

  const stopCurrent = useCallback(() => {
    if (soundRef.current) {
      soundRef.current.stop();
      soundRef.current.unload();
      soundRef.current = null;
    }
    setActiveSound(null);
  }, []);

  const toggleSound = useCallback((soundId) => {
    if (activeSound === soundId) {
      stopCurrent();
    } else {
      if (soundRef.current) {
        soundRef.current.stop();
        soundRef.current.unload();
      }
      const howl = new Howl({
        src: [SOUND_FILES[soundId]],
        loop: true,
        volume: 0.7,
        html5: true,
        preload: true,
        onplayerror: function() {
          howl.once("unlock", function() {
            howl.play();
          });
        },
      });
      howl.play();
      soundRef.current = howl;
      setActiveSound(soundId);
    }
  }, [activeSound, stopCurrent]);

  const startTimer = (m) => { setTimerRemaining(m * 60); setShowTimer(false); };
  const cancelTimer = () => { setTimerRemaining(null); if (timerRef.current) clearInterval(timerRef.current); };

  useEffect(() => {
    if (timerRemaining === null) return;
    if (timerRemaining <= 0) { stopCurrent(); setTimerRemaining(null); return; }
    timerRef.current = setInterval(() => setTimerRemaining((r) => r !== null ? r - 1 : null), 1000);
    return () => clearInterval(timerRef.current);
  }, [timerRemaining, stopCurrent]);

  useEffect(() => {
    return () => {
      if (soundRef.current) { soundRef.current.stop(); soundRef.current.unload(); }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  const activeName = SOUNDS.find((s) => s.id === activeSound)?.name;

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-24 scrollbar-hide overflow-y-auto min-h-screen">
        <div className="mb-6 animate-fadeInUp">
          <h1 className={`font-heading text-3xl font-bold ${textPrimary} ${isDark ? "drop-shadow-lg" : ""}`}>{t("wellnessTitle")}</h1>
          <p className={`${textSecondary} text-sm mt-1`}>{t("wellnessSub")}</p>
        </div>

        {activeSound && (
          <div data-testid="active-sounds-bar" className="glass-card p-3.5 mb-5 flex items-center justify-between animate-fadeIn">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center relative">
                <Volume2 size={16} className="text-emerald-400" />
                <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" style={{ animationDuration: "2s" }} />
              </div>
              <div>
                <p className={`text-sm ${textPrimary} font-semibold`}>{activeName}</p>
                {timerRemaining !== null && <p className={`text-[11px] ${labelColor} font-medium`}>{formatTime(timerRemaining)} {t("remaining")}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {timerRemaining !== null ? (
                <button data-testid="cancel-timer-button" onClick={cancelTimer} className="p-2 rounded-full bg-red-500/15 text-red-400"><X size={14} /></button>
              ) : (
                <button data-testid="set-timer-button" onClick={() => setShowTimer(!showTimer)}
                  className={`px-3 py-1.5 rounded-full ${isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-500/15 text-amber-600"} text-[11px] font-semibold flex items-center gap-1.5`}>
                  <Timer size={12} /> {t("timer")}
                </button>
              )}
              <button data-testid="stop-all-button" onClick={stopCurrent} className="p-2 rounded-full bg-red-500/15 text-red-400"><Square size={14} /></button>
            </div>
          </div>
        )}

        {showTimer && (
          <div data-testid="timer-picker" className="glass-card p-4 mb-5 animate-fadeIn">
            <p className={`text-[10px] ${labelColor} uppercase tracking-[0.15em] font-semibold mb-3`}>{t("autoStop")}</p>
            <div className="grid grid-cols-4 gap-2">
              {[5, 10, 30, 60].map((m) => (
                <button key={m} data-testid={`timer-${m}`} onClick={() => startTimer(m)}
                  className={`py-3 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                    isDark ? "bg-white/8 text-white hover:bg-amber-500/30 border border-white/5" : "bg-slate-100/80 text-slate-700 hover:bg-amber-100 border border-slate-200/50"
                  }`}>
                  {m} min
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {SOUNDS.map((sound, i) => {
            const isPlaying = activeSound === sound.id;
            return (
              <button
                key={sound.id}
                data-testid={`sound-card-${sound.id}`}
                onClick={() => toggleSound(sound.id)}
                className={`relative overflow-hidden rounded-2xl h-40 transition-all duration-300 animate-fadeInUp active:scale-[0.96] ${
                  isPlaying ? "ring-2 ring-amber-400/70 shadow-[0_0_20px_rgba(212,175,55,0.25)] scale-[1.02]" : "hover:scale-[1.02]"
                }`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500"
                  style={{ backgroundImage: `url(${sound.image})`, transform: isPlaying ? "scale(1.1)" : "scale(1)" }} />
                <div className={`absolute inset-0 transition-all duration-300 ${isPlaying ? "bg-black/25" : "bg-black/50"}`} />
                {isPlaying && (
                  <div className="absolute top-3 right-3 flex gap-[3px] items-end">
                    {[1, 2, 3, 4].map((b) => (
                      <div key={b} className="w-[3px] bg-amber-400 rounded-full"
                        style={{ animation: `pulse ${0.5 + b * 0.15}s ease-in-out infinite alternate`, height: `${6 + b * 3}px` }} />
                    ))}
                  </div>
                )}
                <div className="relative z-10 h-full flex flex-col justify-between p-4">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-md transition-all ${
                    isPlaying ? "bg-amber-500 shadow-[0_0_15px_rgba(212,175,55,0.5)]" : "bg-white/15"
                  }`}>
                    {isPlaying ? <Pause size={18} className="text-white" /> : <Play size={18} className="text-white ml-0.5" />}
                  </div>
                  <p className="text-white text-sm font-bold drop-shadow-lg text-left">{sound.name}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <BottomNav />
    </AppLayout>
  );
}
